/**
 * Thread page — bubbles, compose box, reconcile prompt.
 *
 * Layout:
 *   ┌─────────────────────────────┐
 *   │   ThreadHeader              │ ← counterparty name + addr
 *   ├─────────────────────────────┤
 *   │   ConfirmedBubble × N       │ ← scroll area (chronological)
 *   │   PendingBubble × M         │
 *   │ ↑ auto-scrolled to bottom   │
 *   ├─────────────────────────────┤
 *   │   ReconcilePrompt (if any)  │
 *   ├─────────────────────────────┤
 *   │   ComposeBox                │ ← pinned
 *   └─────────────────────────────┘
 *
 * Routing: `wouter` passes `:counterparty` as a URL-decoded display string.
 * We resolve the canonical raw form from the cached inbox row when we have it
 * (so cache keys and pending rows line up); when we don't (deep link case),
 * we fall back to using the display form as the cache key — which still works
 * because the server accepts either form on the route.
 */
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useThread } from "../hooks/useThread";
import { useCompose } from "../hooks/useCompose";
import { usePendingThread } from "../lib/pending";
import { backButton, haptics } from "../lib/telegram";
import { ConfirmedBubble, PendingBubble } from "../components/MessageBubble";
import { ComposeBox } from "../components/ComposeBox";
import { ReconcilePrompt } from "../components/ReconcilePrompt";
import { ThreadHeader } from "../components/ThreadHeader";
import { ThreadSkeleton } from "../components/Skeleton";
import { ThreadEmptyState } from "../components/EmptyState";
import { inboxKey } from "../hooks/queryKeys";
import type { InboxResponse } from "../types/api";

interface Props {
  counterparty: string;
}

export function ThreadPage({ counterparty }: Props): JSX.Element {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  // Resolve raw form via cache so pending bubbles line up.
  const cachedInbox = qc.getQueryData<InboxResponse>(inboxKey());
  const cachedRow = cachedInbox?.threads.find(
    (t) => t.counterparty === counterparty || t.counterparty_raw === counterparty,
  );
  const counterpartyRaw = cachedRow?.counterparty_raw ?? counterparty;
  const nickname = cachedRow?.nickname ?? null;

  // TG BackButton — native back nav.
  useEffect(() => {
    const dispose = backButton.show(() => {
      haptics.selection();
      setLocation("/");
    });
    return () => dispose();
  }, [setLocation]);

  const { data, isLoading, isError } = useThread(counterparty);
  const pending = usePendingThread(counterpartyRaw);
  const { status: sendStatus, errorMessage: sendError, send } = useCompose();

  // Merge confirmed + pending. The server returns messages newest-first
  // (ORDER BY lt DESC), but a chat UI wants oldest-at-top / newest-at-bottom,
  // so we reverse for display. Pending sits at the tail by created_at.
  const allItems = useMemo(() => {
    const confirmed = data?.messages ? [...data.messages].reverse() : [];
    return { confirmed, pending };
  }, [data, pending]);

  // Auto-scroll to bottom on new content. We use a ref-sentinel so it works
  // even when the scroll container's height is changing (compose grows etc.).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastCountRef = useRef(0);
  useEffect(() => {
    const total = allItems.confirmed.length + allItems.pending.length;
    if (total !== lastCountRef.current) {
      lastCountRef.current = total;
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [allItems]);

  function handleSend(args: { comment: string; amount_nano: string }): void {
    void send({
      to: counterparty,
      toRaw: counterpartyRaw,
      comment: args.comment,
      amount_nano: args.amount_nano,
    });
  }

  return (
    <div className="flex h-full flex-col bg-tg-bg">
      <ThreadHeader
        counterparty={counterparty}
        nickname={nickname}
        onBack={() => setLocation("/")}
      />

      <div
        ref={scrollRef}
        className="scroll-area flex-1 overflow-y-auto"
        role="log"
        aria-live="polite"
        aria-label="Conversation"
      >
        {isLoading ? (
          <ThreadSkeleton />
        ) : isError ? (
          <div className="flex h-full items-center justify-center px-8 text-center text-sm text-tg-destructive">
            Could not load this thread.
          </div>
        ) : (
          <div className="flex flex-col gap-2 px-3 py-3">
            {allItems.confirmed.length === 0 && allItems.pending.length === 0 ? (
              <ThreadEmptyState counterparty={counterparty} />
            ) : (
              <>
                {allItems.confirmed.map((m) => (
                  <ConfirmedBubble key={m.tx_hash} message={m} />
                ))}
                {allItems.pending.map((p) => (
                  <PendingBubble key={p.pending_id} message={p} />
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <ReconcilePrompt pendingMessages={pending} />
      <ComposeBox status={sendStatus} onSend={handleSend} errorMessage={sendError} />
    </div>
  );
}
